import {Component, Expressions, Types} from '@packages/systems/wfdl';
import {
  EList,
  type Expression,
  type ExpressionElement,
} from '@packages/systems/wfdl-ast';
import {emptyArray} from '@packages/utilities/fp/utils';
import {weakMemo} from '@packages/utilities/memo';

export const emptySlotDefaultList = EList([]);

// A slot field's default expression lives on the component's `defaultData`,
// under the function's returned record at the field's id.
export const getSlotDefaultExpression = (
  component: Pick<Component.ComponentType, 'dataType' | 'defaultData'>,
  slotFieldId: string
): Expression | null => {
  // Guard first: a component with no defaultData is unmigrated, and returning
  // null is what routes its reads to the legacy system.defaultValue shape.
  const defaultData = component.defaultData;
  if (!defaultData) return null;

  const expression = Expressions.getIn(defaultData, [
    {in: 'Function'},
    {in: 'Record', at: slotFieldId},
  ]);
  if (expression) return expression;

  // A slot field that exists but holds nothing has an empty default, not a
  // missing one. Only an unknown field has no answer.
  const field = Types.getRecordFields(component.dataType)?.[slotFieldId];
  if (!field) return null;
  return emptySlotDefaultList;
};

// Writes into an EXISTING defaultData only -- its presence is what marks a
// component migrated, and a seeded partial one reads as the component's
// complete set of field defaults (see getEffectiveDefaultDataFields), stranding
// every default still held on system.defaultValue. Callers check enrollment.
export const setSlotDefaultExpression = (
  defaultData: Expression,
  slotFieldId: string,
  expression: Expression
): Expression =>
  Expressions.sparseSetIn(
    defaultData,
    [{in: 'Function'}, {in: 'Record', at: slotFieldId}],
    expression
  );

// Per-field default elements read straight off `defaultData`. Already
// parsed expressions, so this needs no plugins and no reification.
// Callers creating wrappers need to retain them or memoize their own result,
// since weakMemo keys on the component object's identity.
export const getSlotDefaultElementsByFieldId = weakMemo(
  (component: Pick<Component.ComponentType, 'dataType' | 'defaultData'>) => {
    const defaults = new Map<string, ExpressionElement[]>();
    const fields = Types.getRecordFields(component.dataType);
    if (!fields) return defaults;

    for (const [fieldId, fieldType] of Object.entries(fields)) {
      if (!Types.isSlotContentType(fieldType)) continue;

      const defaultExpression = getSlotDefaultExpression(component, fieldId);
      const expressions = defaultExpression
        ? Expressions.getList(defaultExpression)
        : null;
      if (!expressions) continue;

      const elements: ExpressionElement[] = [];
      for (const expression of expressions) {
        const element = Expressions.getElement(expression);
        if (element) elements.push(element);
      }
      defaults.set(fieldId, elements.length > 0 ? elements : emptyArray);
    }

    return defaults;
  }
);

/**
 * Whether the component authors default content on any of its SlotContent
 * fields. Reads the at-rest shape from `defaultData` only, so it answers
 * without reifying.
 *
 * Only migrated defaults on `defaultData` count here. The legacy
 * `system.defaultValue` shape has legit inert data in prod on components that
 * have already been shared via libraries, so gating export on it would block
 * components that install just fine today.
 */
export const hasSlotDefaultContent = (
  component: Component.ComponentType
): boolean => {
  const fields = Types.getRecordFields(Component.getDataType(component));
  if (!fields) return false;
  return Object.keys(fields).some((fieldId) => {
    const defaultElements =
      getSlotDefaultElementsByFieldId(component).get(fieldId);
    return Boolean(defaultElements && defaultElements.length > 0);
  });
};

// Skips the legacy `system.defaultValue` shape, like hasSlotDefaultContent.
export const getSlotDefaultLists = (
  component: Component.ComponentType
): Array<[string, Expression]> => {
  const lists: Array<[string, Expression]> = [];
  for (const [fieldId, elements] of getSlotDefaultElementsByFieldId(
    component
  )) {
    if (elements.length === 0) continue;
    const list = getSlotDefaultExpression(component, fieldId);
    if (list) lists.push([fieldId, list]);
  }
  return lists;
};

// Returns the same component when no list changes.
export const mapSlotDefaultLists = (
  component: Component.ComponentType,
  mapList: (list: Expression, fieldId: string) => Expression
): Component.ComponentType => {
  let defaultData = Component.getDefaultData(component);
  if (!defaultData) return component;

  let changed = false;
  for (const [fieldId, list] of getSlotDefaultLists(component)) {
    const nextList = mapList(list, fieldId);
    if (nextList === list) continue;
    defaultData = setSlotDefaultExpression(defaultData, fieldId, nextList);
    changed = true;
  }
  if (!changed) return component;
  return Component.update(component, {defaultData});
};

// Runs one transform over the render and every slot default list as a single
// list expression, for transforms with per-call side effects such as importing
// origin styles, which would duplicate if run once per tree.
export const mapRenderAndSlotDefaultLists = (
  component: Component.ComponentType,
  render: Expression,
  transform: (trees: Expression, includesSlotDefaults: boolean) => Expression
): {render: Expression; component: Component.ComponentType} => {
  const lists = getSlotDefaultLists(component);
  if (lists.length === 0) return {render: transform(render, false), component};

  const transformed = Expressions.getList(
    transform(EList([render, ...lists.map(([, list]) => list)]), true)
  );
  if (!transformed || transformed.length !== lists.length + 1) {
    throw new Error('Transform must preserve the number of trees');
  }

  const [nextRender, ...nextLists] = transformed as [
    Expression,
    ...Expression[],
  ];
  const nextListByFieldId = new Map(
    lists.map(([fieldId], index) => [fieldId, nextLists[index]!])
  );
  return {
    render: nextRender,
    component: mapSlotDefaultLists(
      component,
      (list, fieldId) => nextListByFieldId.get(fieldId) ?? list
    ),
  };
};
